/**
 * Curated IATA code → city display name for the `en` and `es` message locales — the
 * non-pt-BR siblings of `AIRPORT_CITIES`, read by `localizedCityByAirport`.
 *
 * Same override discipline as the pt-BR tier: the generated world map
 * (`AIRPORT_CITIES_WORLD`, English/local dataset spelling) covers every other airport, so an
 * entry earns its keep only when that spelling is wrong for a reader of the locale:
 *
 *  1. the dataset names the MUNICIPALITY, not the city served (`Arnavutkoy` → `Istanbul`,
 *     `Tocumen` → `Panama City`, `Dulles` → `Washington`, `Ezeiza` → `Buenos Aires`), or
 *     strings several places together (`Bale/Mulhouse` → `Basel`);
 *  2. English exonym the dataset lacks (`Venezia` → `Venice`);
 *  3. (`es` only) Spanish exonym (`New York` → `Nueva York`, `London` → `Londres`).
 *
 * NOT listed: Brazilian airports and curated entries that only add accents/casing to the
 * dataset (`Bogotá`, `Cancún`) — those resolve to the curated pt-BR name, which is the city's
 * own name in every locale (`nativeCuratedCity` in `airport_city.ts`).
 *
 * `es` falls back to `en` before the world map, so an `es` entry only exists where Spanish
 * differs from the `en` value. Airports of one metro must agree, as in `AIRPORT_CITIES`.
 */
export const AIRPORT_CITIES_EN: Record<string, string> = {
  EZE: 'Buenos Aires',
  ASU: 'Asunción',
  LPB: 'La Paz',
  MDE: 'Medellín',
  PTY: 'Panama City',
  IAD: 'Washington',
  VCE: 'Venice',
  FRA: 'Frankfurt',
  IST: 'Istanbul',
  // #4157: aeroportos de uma cidade com mais de um concordam também aqui — o dataset
  // grava DFW como "Dallas-Fort Worth" e CIA como "Roma" (FCO é "Rome").
  DFW: 'Dallas',
  CIA: 'Rome',
  // Lugares juntos ("Bordeaux/Merignac") ou o município do aeroporto ("Santa Clara" no SAL),
  // os mesmos do `AIRPORT_CITIES`.
  BOD: 'Bordeaux',
  BSL: 'Basel',
  MLH: 'Basel',
  CAY: 'Cayenne',
  LSC: 'La Serena',
  PBM: 'Paramaribo',
  SAL: 'San Salvador',
  SAP: 'San Pedro Sula',
  RDU: 'Raleigh',
  CVG: 'Cincinnati',
  BJX: 'León',
};

export const AIRPORT_CITIES_ES: Record<string, string> = {
  PTY: 'Ciudad de Panamá',
  JFK: 'Nueva York',
  LGA: 'Nueva York',
  MEX: 'Ciudad de México',
  LIS: 'Lisboa',
  LHR: 'Londres',
  LGW: 'Londres',
  LCY: 'Londres',
  STN: 'Londres',
  LTN: 'Londres',
  FCO: 'Roma',
  // CIA acompanha FCO: sem esta entrada, o es cairia no "Rome" do en.
  CIA: 'Roma',
  MXP: 'Milán',
  LIN: 'Milán',
  VCE: 'Venecia',
  AMS: 'Ámsterdam',
  BRU: 'Bruselas',
  FRA: 'Fráncfort',
  MUC: 'Múnich',
  BER: 'Berlín',
  ZRH: 'Zúrich',
  GVA: 'Ginebra',
  VIE: 'Viena',
  CPH: 'Copenhague',
  ARN: 'Estocolmo',
  IST: 'Estambul',
  BOD: 'Burdeos',
  BSL: 'Basilea',
  MLH: 'Basilea',
  CAY: 'Cayena',
  JNB: 'Johannesburgo',
  NRT: 'Tokio',
  HND: 'Tokio',
  ICN: 'Seúl',
  // #4157: GMP acompanha ICN (o dataset grava "Seoul").
  GMP: 'Seúl',
  PEK: 'Pekín',
  PVG: 'Shanghái',
  SIN: 'Singapur',
};

/** Metro ("all airports") codes, per locale — same keys as `METRO_AREAS`. */
export const METRO_CITIES_EN: Record<string, string> = {
  SAO: 'São Paulo',
  RIO: 'Rio de Janeiro',
  BHZ: 'Belo Horizonte',
  BUE: 'Buenos Aires',
  NYC: 'New York',
  WAS: 'Washington',
  LON: 'London',
  PAR: 'Paris',
  MIL: 'Milan',
};

export const METRO_CITIES_ES: Record<string, string> = {
  NYC: 'Nueva York',
  LON: 'Londres',
  MIL: 'Milán',
};
